import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/nav";

interface NotificationCounts {
  total: number;
  invitations: number;
  reports: number;
}

/**
 * Lightweight unread/actionable counter for the topbar bell.
 * Role-aware so we don't issue queries the user can't run.
 */
export function useNotifications(role: AppRole) {
  const { user } = useAuth();
  const [counts, setCounts] = useState<NotificationCounts>({ total: 0, invitations: 0, reports: 0 });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        let invitations = 0;
        let reports = 0;

        if (role === "student" && user.email) {
          const [tests, parents] = await Promise.all([
            supabase
              .from("test_invitations")
              .select("id", { count: "exact", head: true })
              .eq("email", user.email)
              .is("completed_at", null),
            supabase
              .from("parent_invitations")
              .select("id", { count: "exact", head: true })
              .eq("student_email", user.email)
              .eq("status", "pending"),
          ]);
          invitations = (tests.count ?? 0) + (parents.count ?? 0);
        } else if (role === "admin") {
          const r = await supabase
            .from("question_reports")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending");
          reports = r.count ?? 0;
        }

        if (!cancelled) {
          setCounts({ total: invitations + reports, invitations, reports });
        }
      } catch {
        // swallow — bell is non-critical
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, role]);

  return counts;
}
